import {
  ChartPie,
  Fire,
  GlobeHemisphereWest,
  Link,
  LinkedinLogo,
  PencilSimpleLine,
  SealCheck,
  SlidersHorizontal,
  Trash,
} from "@phosphor-icons/react";

import type { UserPermissions, WorkspaceAccount } from "common/types";

import AccountProxyTag from "./components/AccountTags/AccountProxyTag";
import AccountSsiTag from "./components/AccountTags/AccountSsiTag";
import LicenseTag from "./components/AccountTags/LicenseTag";
import LoginStatusTag from "./components/AccountTags/LoginStatusTag";
import WarmupState from "./components/AccountTags/WarmupState";

import type { Icon } from "@phosphor-icons/react";
import type { JSX } from "react";
import type { AccountSearchParams } from "./types";

type FiltersType<Keys extends keyof AccountSearchParams> = {
  [K in Keys]: {
    icon: JSX.Element;
    label: string;
    options: Record<AccountSearchParams[K][number], string>;
  };
};

export const ACCOUNTS_FILTERS: FiltersType<keyof AccountSearchParams> = {
  state: {
    icon: <Link weight="bold" />,
    label: "Login status",
    options: {
      LoggedIn: "Connected",
      LoggedOut: "Disconnected",
    },
  },
  seat: {
    icon: <SealCheck weight="fill" />,
    label: "Seat",
    options: {
      outreach: "Outreach",
      rented: "Avatar",
      "no-seat": "No seat",
    },
  },
  linkedinSubscription: {
    icon: <LinkedinLogo weight="fill" />,
    label: "LinkedIn plan",
    options: {
      "sales-navigator": "Sales Navigator",
      free: "Free account",
      premium: "Premium",
    },
  },
};

export const MANAGE_ACCOUNTS_ACTIONS = {
  proxy: {
    icon: GlobeHemisphereWest,
    getLabel: () => "Change proxies",
    getEligibleItems: (selection: WorkspaceAccount[]) =>
      selection.filter(({ type }) => type !== "rental"),
  },
  interactions: {
    icon: SlidersHorizontal,
    getLabel: () => "Manage interactions",
    getEligibleItems: (selection: WorkspaceAccount[]) =>
      selection.filter(({ type }) => type !== "rental"),
  },
  seats: {
    icon: SealCheck,
    getLabel: (eligibleItemsCount: number) =>
      eligibleItemsCount === 1
        ? "Assign seat"
        : `Assign ${eligibleItemsCount} seats`,
    getEligibleItems: (selection: WorkspaceAccount[]) =>
      selection.filter(({ type }) => type !== "rental"),
  },
} as const;

interface AccountColumn {
  label: string;
  icon: Icon;
  /** Preselected in the mobile tags drawer. */
  isDefault?: true;
  /** Renders the column's value */
  renderTag: (account: WorkspaceAccount, isDesktop: boolean) => JSX.Element;
}

/**
 * Single source of truth for the accounts columns, consumed by both the desktop
 * table (`DesktopLayout/DesktopTable`) and the mobile list
 * (`MobileLayout/MobileList`). Keys are the persisted column visibility ids.
 */
export const ACCOUNTS_COLUMNS = {
  warmup: {
    label: "Warmup",
    icon: Fire,
    renderTag: ({ id }, isDesktop) => (
      <WarmupState accountId={id} isTag={!isDesktop} />
    ),
  },
  proxy: {
    label: "Proxy",
    icon: GlobeHemisphereWest,
    isDefault: true,
    renderTag: (account, isDesktop) => (
      <AccountProxyTag account={account} size={isDesktop ? "md" : "sm"} />
    ),
  },
  ssi: {
    label: "SSI",
    icon: ChartPie,
    renderTag: (account, isDesktop) => (
      <AccountSsiTag account={account} isTag={!isDesktop} />
    ),
  },
  seat: {
    label: "Seat",
    icon: SealCheck,
    isDefault: true,
    renderTag: ({ type, license }, isDesktop) => (
      <LicenseTag
        isRented={type === "rental"}
        license={license}
        size={isDesktop ? "md" : "sm"}
      />
    ),
  },
  status: {
    label: "Login status",
    icon: Link,
    isDefault: true,
    renderTag: (account, isDesktop) => (
      <LoginStatusTag account={account} size={isDesktop ? "md" : "sm"} />
    ),
  },
} satisfies Record<string, AccountColumn>;

export const SSI_ITEMS = {
  PROFESSIONAL_BRAND: {
    label: "Establish your professional brand",
    bgClassName: "bg-yellow-400",
    textClassName: "text-yellow-600",
    fillClassName: "fill-yellow-400",
  },
  FIND_RIGHT_PEOPLE: {
    label: "Find the right people",
    bgClassName: "bg-purple-500",
    textClassName: "text-purple-600",
    fillClassName: "fill-purple-500",
  },
  INSIGHT_ENGAGEMENT: {
    label: "Engage with insights",
    bgClassName: "bg-orange-500",
    textClassName: "text-orange-600",
    fillClassName: "fill-orange-400",
  },
  STRONG_RELATIONSHIP: {
    label: "Build relationships",
    bgClassName: "bg-blue-400",
    textClassName: "text-blue-600",
    fillClassName: "fill-blue-400",
  },
} as const;

export const PROXY_ACTIONS = {
  edit: { icon: PencilSimpleLine, label: "Edit proxy" },
  delete: { icon: Trash, label: "Delete proxy", isDanger: true },
} as const;

export const ACCOUNTS_TAB_ITEMS: {
  label: string;
  path: string;
  visibleForPermissions?: UserPermissions;
}[] = [
  { label: "Accounts", path: "" },
  {
    label: "Proxies",
    path: "proxies",
    visibleForPermissions: ["accounts_full"],
  },
  /*{ label: "Labels", path: "labels", visibleForPermissions: ["accounts_full"] },*/
];
